<script>
import { formButton, formInput } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { ref, provide, onMounted, computed, watch } from 'vue'
import { useMeta } from 'vue-meta'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import * as yup from 'yup'

import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { validateStepAccess } from '@/composables/utils/onboardingGuards'
import { getPlatformFromUrl, platformWhiteLabel } from '@/composables/utils/useWhitelabelService.js'
import { checkCode } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { useOnboardingStore } from '@/stores/onboarding'
import { CLIENT_DEMAND_REASON } from '@/constants/clientDemandTypes'

export default {
  name: 'RegisterPFStep2',
  components: {
    RegisterLayout,
    formInput,
    formButton,
    StepProgressBar
  },

  setup() {
    useMeta({ title: 'Cadastro PF - Etapa 2' })

    const router = useRouter()
    const onboardingStore = useOnboardingStore()

    onMounted(() => {
      validateStepAccess(onboardingStore, 'PF', [1], router)
      showOtherDemandInput.value = isOtherDemand(demandValue.value)
    })

    const step2Data = onboardingStore.getStepData(2) || {}

    const subscriptionCodeValue = ref(step2Data.subscriptionCode || '')
    const subscriptionCodeType = ref('')

    const hasCodePartner = computed(() => {
      return platformWhiteLabel(getPlatformFromUrl(), 'codePartner') != null
    })

    const loading = ref(false)

    const sellingOptionValue = ref(step2Data.sellingOption || null)
    const demandValue = ref(step2Data.demand || '')
    const customDemandValue = ref(step2Data.customDemand || '')

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    watch(sellingOptionValue, () => {
      demandValue.value = ''
      customDemandValue.value = ''
    })

    watch(sellingOptionValue, (val) => {
      setFieldValue('sellingOption', val)
    })

    watch(demandValue, (val) => {
      setFieldValue('demand', val)
    })

    const { handleSubmit, errors, setFieldValue } = useForm({
      validationSchema: yup.object({
        subscriptionCode: yup.string(),
        sellingOption: yup.boolean().required('Campo obrigatório'),
        demand: yup.string().required('Selecione um motivo para operações de câmbio'),
        customDemand: yup.string().test(function (value) {
          if (demandValue.value === 'Outros') {
            return !!value
          }
          return true
        })
      }),
      initialValues: {
        sellingOption: sellingOptionValue.value,
        demand: demandValue.value
      }
    })

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true

      const isInvalidCode = await validateCode()
      if (isInvalidCode) return

      onboardingStore.setStepData(2, {
        subscriptionCode: subscriptionCodeValue.value,
        subscriptionCodeType: subscriptionCodeType.value,
        sellingOption: sellingOptionValue.value,
        demand: demandValue.value,
        customDemand: customDemandValue.value
      })

      router.push('/register/pf/step-3')
    })

    const statusResponse = ref(null)
    const invalidCodeMessage = ref('')

    const validateCode = async () => {
      if (!subscriptionCodeValue.value?.trim()) return false

      try {
        const bodySubscriptionCode = {
          client_type: onboardingStore.typeClient,
          refer_code: encodeURIComponent(subscriptionCodeValue.value.trim()),
          institution: platformWhiteLabel(getPlatformFromUrl(), 'institution').toUpperCase()
        }

        const { data } = await checkCode(bodySubscriptionCode)
        statusResponse.value = data?.payload?.status

        const statusMessages = {
          EXPIRED: 'Código expirado!',
          DOES_NOT_EXIST: 'Código não encontrado!',
          INVALID_TYPE:
            onboardingStore.typeClient === 'pj'
              ? 'Código válido apenas para cadastros PF.'
              : 'Código válido apenas para cadastros PJ.',
          INVALID_PARTNER: 'Código válido, contacte o suporte.',
          INVALID_PROMOCODE: 'Código válido, contacte o suporte.'
        }

        if (statusMessages[statusResponse.value]) {
          invalidCodeMessage.value = statusMessages[statusResponse.value]
          toast(invalidCodeMessage.value, { autoClose: 3000, theme: 'auto', type: 'error' })
          return true
        }
        subscriptionCodeType.value = data?.payload?.discountType

        return false
      } catch (error) {
        handleGlobalError(error, 'Erro ao validar o código:')
        toast('Erro ao validar o código. Tente novamente mais tarde.', {
          autoClose: 3000,
          theme: 'auto',
          type: 'error'
        })
        return true
      } finally {
        loading.value = false
      }
    }

    const receivingDemands = [
      {
        label: 'Recebimento de serviços prestados',
        value: CLIENT_DEMAND_REASON.RECEIVING_SERVICES
      },
      {
        label: 'Recebimento de investimento, lucros ou dividendos',
        value: CLIENT_DEMAND_REASON.RECEIVING_INVESTMENT
      },
      { label: 'Doações', value: CLIENT_DEMAND_REASON.DONATIONS },
      { label: 'Outros', value: CLIENT_DEMAND_REASON.RECEIVING_OTHER }
    ]

    const sendingDemands = [
      { label: 'Envio para familiares', value: CLIENT_DEMAND_REASON.SENDING_FAMILY },
      { label: 'Compra de serviços no exterior', value: CLIENT_DEMAND_REASON.SERVICES_PURCHASE },
      { label: 'Investimento', value: CLIENT_DEMAND_REASON.INVESTMENT },
      { label: 'Outros', value: CLIENT_DEMAND_REASON.SENDING_OTHER }
    ]

    const demandOptions = computed(() => {
      return sellingOptionValue.value === true ? receivingDemands : sendingDemands
    })

    const showOtherDemandInput = ref(false)

    watch(demandValue, (val) => {
      showOtherDemandInput.value = isOtherDemand(val)
    })

    const isOtherDemand = (value) =>
      value === CLIENT_DEMAND_REASON.SENDING_OTHER || value === CLIENT_DEMAND_REASON.RECEIVING_OTHER

    const isSellingSelected = computed(() => sellingOptionValue.value !== null)

    return {
      hasCodePartner,
      subscriptionCodeValue,
      loading,
      onSubmitAction,
      statusResponse,

      sellingOptionValue,
      demandValue,
      demandOptions,
      customDemandValue,
      isSellingSelected,

      alreadySubmittedOnce,
      errors,
      showOtherDemandInput
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar class="mt-5 mb-5" :steps="['Dados', 'Demanda', 'Senha']" :current="2" />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <form-input
            v-if="!hasCodePartner"
            class="col-12 form-input mb-3"
            label="Possui código de indicação/promocional? (opcional)"
            v-model="subscriptionCodeValue"
            placeholder="#EXEMPLO"
            id="subscriptionCode"
            name="subscriptionCode"
            type="text"
            :isInvalid="
              [
                'DOES_NOT_EXIST',
                'EXPIRED',
                'INVALID_TYPE',
                'INVALID_PARTNER',
                'INVALID_PROMOCODE'
              ].includes(statusResponse)
            "
          />

          <div class="col-12 mt-4">
            <p class="text-center">Você busca uma solução para:</p>
            <div class="radio-card-group">
              <label class="radio-card-label" :class="{ selected: sellingOptionValue === true }">
                <input
                  type="radio"
                  :value="true"
                  v-model="sellingOptionValue"
                  name="sellingOption"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Receber valores</span>
              </label>

              <label class="radio-card-label" :class="{ selected: sellingOptionValue === false }">
                <input
                  type="radio"
                  :value="false"
                  v-model="sellingOptionValue"
                  name="sellingOption"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Enviar valores</span>
              </label>
            </div>
          </div>
          <div
            v-if="alreadySubmittedOnce && errors.sellingOption"
            class="text-danger text-center error"
          >
            {{ errors.sellingOption }}
          </div>

          <div v-if="sellingOptionValue != null" class="col-12 mt-3">
            <label class="mb-2 d-block fw-bold">
              {{
                sellingOptionValue
                  ? 'Qual o motivo dos recebimentos em moeda estrangeira?'
                  : 'Qual o motivo dos envios em moeda estrangeira?'
              }}
            </label>

            <div class="radio-card-group stacked">
              <label
                v-for="(option, index) in demandOptions"
                :key="index"
                class="radio-card-label"
                :class="{ selected: demandValue === option.value }"
              >
                <input
                  type="radio"
                  :value="option.value"
                  v-model="demandValue"
                  id="demand"
                  name="demand"
                />
                <span class="custom-radio"></span>
                <span class="label-text">{{ option.label }}</span>
              </label>
            </div>

            <div v-if="alreadySubmittedOnce && errors.demand" class="text-danger text-center error">
              {{ errors.demand }}
            </div>
            <form-input
              v-if="showOtherDemandInput"
              class="col-12 form-input mt-2"
              label="Descreva qual o motivo para suas operações de câmbio"
              placeholder="Ex: Pagamento de dívida no exterior"
              v-model="customDemandValue"
              id="customDemand"
              name="customDemand"
              type="text"
            />
          </div>

          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
